import { Suspense } from "react";
import { Outlet, useParams } from "react-router-dom";
import ProfileChatWidget from "./ProfileChatWidget";
import ProfileLoadingState from "./ProfileLoadingState";
import { ProfileStudyContext, useProfileStudy } from "./useProfileStudy";

/**
 * Thin layout wrapper shared by every profile sub-tab (Profile / Platform
 * Profile / Interviews / Timeline / Onboarding / Opportunities / External
 * Links). Its ONLY job is to keep the "Ask about this candidate" chat widget
 * mounted while the tab (the <Outlet/>) swaps — previously the widget lived
 * inside ProfilePage and disappeared the moment you switched tabs.
 *
 * It renders no chrome of its own: each tab page still renders its own
 * ProfileSidebar + content exactly as before, so this is visually a no-op
 * apart from the persistent widget. Keyed by profile id so switching to a
 * different candidate resets the conversation.
 *
 * The local <Suspense> wraps only the <Outlet/> (the tab content) so that
 * loading a not-yet-fetched tab chunk shows the profile loading state in place of the tab
 * WITHOUT unmounting the widget — the app-level Suspense boundary would blank
 * the whole page (widget included) on a lazy chunk load.
 *
 * It also follows a profile opened from the matching page across its tabs
 * (useProfileStudy: how long it was read, which tabs and sections), for the
 * same reason: it outlives the tab swaps.
 */
function TabFallback() {
  return <ProfileLoadingState loading />;
}

export default function ProfileLayout() {
  const { id } = useParams<{ id: string }>();
  const study = useProfileStudy(id);
  return (
    <ProfileStudyContext.Provider value={study}>
      <Suspense fallback={<TabFallback />}>
        <Outlet />
      </Suspense>
      {id ? <ProfileChatWidget key={id} profileId={id} /> : null}
    </ProfileStudyContext.Provider>
  );
}
